import React from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { Phone, MapPin, CheckCircle, ArrowRight, Star } from 'lucide-react';
import './LocationPage.css';

// Location data for SEO pages
const LOCATION_DATA = {
  'goettingen': {
    name: 'Göttingen',
    intro: 'Wir sind regelmäßig in Göttingen und den umliegenden Stadtteilen im Einsatz. Ob in Weende, Geismar oder der Innenstadt – kurze Wege bedeuten schnelle Termine.',
    description: 'Als Universitätsstadt hat Göttingen viele gepflegte Gärten und Grundstücke. Wir kennen die Gegebenheiten vor Ort und arbeiten oft mehrmals pro Woche in verschiedenen Stadtteilen.',
    neighborhoods: ['Weende', 'Geismar', 'Grone', 'Nikolausberg', 'Herberhausen'],
    recentProjects: [
      { title: 'Gartenpflege in Weende', desc: 'Regelmäßige Pflege eines großen Grundstücks mit Obstbäumen.' },
      { title: 'Baumfällung in Geismar', desc: 'Kranke Kastanie in Hausnähe sicher gefällt und entsorgt.' }
    ]
  },
  'bovenden': {
    name: 'Bovenden',
    intro: 'Bovenden liegt direkt vor unserer Haustür. Wir sind hier fast täglich unterwegs und kennen viele Kunden persönlich.',
    description: 'Die Gemeinde Bovenden mit ihren Ortsteilen gehört zu unserem Kerngebiet. Ob Eddigehausen, Harste oder der Hauptort – wir sind schnell bei Ihnen.',
    neighborhoods: ['Eddigehausen', 'Harste', 'Lenglern', 'Reyershausen'],
    recentProjects: [
      { title: 'Heckenrückschnitt', desc: 'Große Thujahecke eingekürzt und in Form gebracht.' },
      { title: 'Terrassenreinigung', desc: 'Natursteinpflaster von Moos befreit – sieht aus wie neu.' }
    ]
  },
  'northeim': {
    name: 'Northeim',
    intro: 'In Northeim und Umgebung sind wir mehrmals pro Woche im Einsatz. Die Fahrt dauert nur etwa 20 Minuten.',
    description: 'Northeim mit seinem historischen Stadtkern hat viele Gärten, die regelmäßige Pflege brauchen. Wir betreuen hier mehrere Stammkunden.',
    neighborhoods: ['Northeim-Stadt', 'Höckelheim', 'Sudheim', 'Imbshausen'],
    recentProjects: [
      { title: 'Sturmschaden beseitigt', desc: 'Umgestürzter Baum nach Unwetter noch am selben Tag entfernt.' },
      { title: 'Gartenneugestaltung', desc: 'Verwildertes Grundstück komplett aufgeräumt und vorbereitet.' }
    ]
  },
  'einbeck': {
    name: 'Einbeck',
    intro: 'Auch in Einbeck und der südlichen Region sind wir regelmäßig tätig. Gerne kommen wir auch zu Ihnen.',
    description: 'Die Fachwerkstadt Einbeck hat besondere Anforderungen an Gartenarbeiten. Enge Gassen und historische Grundstücke sind für uns kein Problem.',
    neighborhoods: ['Einbeck-Stadt', 'Kreiensen', 'Greene', 'Dassel'],
    recentProjects: [
      { title: 'Baggerarbeiten für Carport', desc: 'Fundament ausgehoben und Fläche vorbereitet.' },
      { title: 'Grünschnitt-Entsorgung', desc: 'Kompletter Garten entrümpelt und alles fachgerecht entsorgt.' }
    ]
  },
  'uslar': {
    name: 'Uslar',
    intro: 'Im Solling und rund um Uslar sind wir ebenfalls unterwegs. Die landschaftlich schöne Gegend hat viele große Grundstücke.',
    description: 'Uslar und die umliegenden Dörfer im Solling gehören zu unserem erweiterten Servicegebiet. Für größere Aufträge kommen wir gerne auch hierher.',
    neighborhoods: ['Uslar-Stadt', 'Volpriehausen', 'Schönhagen', 'Delliehausen'],
    recentProjects: [
      { title: 'Forstarbeiten', desc: 'Mehrere Bäume auf Privatgrundstück gefällt und aufgearbeitet.' },
      { title: 'Gartenpflege', desc: 'Großes Grundstück am Waldrand regelmäßig gepflegt.' }
    ]
  },
  'noerten-hardenberg': {
    name: 'Nörten-Hardenberg',
    intro: 'Nörten-Hardenberg ist unser Heimatort. Hier kennen wir jeden Winkel und sind täglich vor Ort.',
    description: 'Als unser Firmensitz ist Nörten-Hardenberg unser Haupteinsatzgebiet. Wir sind hier verwurzelt und kennen viele Kunden seit Jahren.',
    neighborhoods: ['Nörten-Hardenberg', 'Angerstein', 'Bishausen', 'Parensen'],
    recentProjects: [
      { title: 'Langzeit-Gartenpflege', desc: 'Seit 5 Jahren betreuen wir diesen Garten – wie unseren eigenen.' },
      { title: 'Notfall-Einsatz', desc: 'Sturmschaden am Wochenende – wir waren sofort da.' }
    ]
  },
  'hardegsen': {
    name: 'Hardegsen',
    intro: 'In Hardegsen und den umliegenden Ortschaften sind wir regelmäßig im Einsatz. Nur wenige Kilometer entfernt.',
    description: 'Die kleine Stadt Hardegsen mit ihrer historischen Burg hat viele gepflegte Privatgärten. Wir freuen uns auf Aufträge aus dieser Gegend.',
    neighborhoods: ['Hardegsen-Stadt', 'Hettensen', 'Üssinghausen', 'Ertinghausen'],
    recentProjects: [
      { title: 'Heckenpflege', desc: 'Lange Ligusterhecke professionell geschnitten.' },
      { title: 'Rasenservice', desc: 'Wöchentlicher Rasenschnitt für berufstätige Familie.' }
    ]
  },
  'moringen': {
    name: 'Moringen',
    intro: 'Auch in Moringen und den umliegenden Dörfern sind wir gerne für Sie da. Die Anfahrt ist kurz.',
    description: 'Moringen liegt verkehrsgünstig und ist für uns gut erreichbar. Wir betreuen hier bereits mehrere zufriedene Kunden.',
    neighborhoods: ['Moringen-Stadt', 'Fredelsloh', 'Großenrode', 'Nienhagen'],
    recentProjects: [
      { title: 'Obstbaumschnitt', desc: 'Alte Apfelbäume fachgerecht zurückgeschnitten.' },
      { title: 'Gartenräumung', desc: 'Grundstück für Hausverkauf vorbereitet.' }
    ]
  }
};

const LocationPage = () => {
  const { location: paramLocation } = useParams();
  const routerLocation = useLocation();
  
  // Get location from URL path (remove leading slash)
  const locationSlug = paramLocation || routerLocation.pathname.replace('/', '');
  const locationData = LOCATION_DATA[locationSlug];

  React.useEffect(() => {
    window.scrollTo(0, 0);
    if (locationData) {
      document.title = `Gartenservice in ${locationData.name} | Nessenius Garten & Landschaftsbau`;
      let metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.setAttribute('content', `Professioneller Gartenservice in ${locationData.name}: Gartenpflege, Erdarbeiten, Baumfällung, Hochdruckreinigung. Ihr lokaler Partner. Jetzt anrufen: 0176-55669090`);
    }
  }, [locationSlug, locationData]);

  if (!locationData) {
    return (
      <div className="location-page">
        <div className="container" style={{ padding: '100px 0', textAlign: 'center' }}>
          <h1>Ort nicht gefunden</h1>
          <p>Dieser Ort ist nicht in unserem Servicegebiet.</p>
          <Link to="/servicegebiet" style={{ color: 'var(--brand-dark)' }}>
            → Unser Servicegebiet ansehen
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="location-page">
      {/* SEO Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": `Nessenius Garten & Landschaftsbau ${locationData.name}`,
          "description": `Gartenpflege, Baumfällung und mehr in ${locationData.name}. Zuverlässig, fair, lokal.`,
          "address": {
            "@type": "PostalAddress",
            "addressLocality": locationData.name,
            "addressRegion": "Niedersachsen",
            "addressCountry": "DE"
          },
          "telephone": "+49 176 55669090",
          "areaServed": locationData.name,
          "priceRange": "$$"
        })}
      </script>

      {/* Hero */}
      <section className="location-hero">
        <div className="container">
          <div className="location-hero-content">
            <div className="location-badge">
              <MapPin size={20} />
              <span>Servicegebiet</span>
            </div>
            <h1>Gartenservice in {locationData.name}</h1>
            <p className="location-intro">{locationData.intro}</p>
            <div className="location-cta">
              <Link to="/kontakt" className="btn-cta">
                Kostenloses Angebot für {locationData.name}
              </Link>
              <a href="tel:+4917655669090" className="btn-secondary-dark">
                <Phone size={18} />
                0176 - 556 690 90
              </a>
            </div>
            <p className="cta-micro">Antwort meist am selben Tag</p>
          </div>
        </div>
      </section>

      {/* About this location */}
      <section className="location-about">
        <div className="container">
          <div className="location-about-grid">
            <div className="location-about-text">
              <h2>Ihr Gartenservice in {locationData.name}</h2>
              <p>{locationData.description}</p>
              <p>
                Wir betreuen Kunden in allen Ortsteilen: {locationData.neighborhoods.join(', ')}.
              </p>
            </div>
            <div className="location-trust-box">
              <h3>Das bekommen Sie</h3>
              <ul>
                <li><CheckCircle size={18} /> Kostenlose Vor-Ort Beratung</li>
                <li><CheckCircle size={18} /> Festpreis ohne Überraschungen</li>
                <li><CheckCircle size={18} /> Kurze Anfahrtswege</li>
                <li><CheckCircle size={18} /> Schnelle Termine</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Services for this location */}
      <section className="location-services">
        <div className="container">
          <h2>Unsere Leistungen in {locationData.name}</h2>
          <div className="location-services-grid">
            <div className="location-service-card">
              <span className="service-emoji">🌿</span>
              <h3>Gartenpflege</h3>
              <p>Rasenmähen, Heckenschnitt, Beetpflege – regelmäßig oder einmalig. Ihr Garten in {locationData.name} bleibt gepflegt.</p>
              <Link to="/leistungen/gartenpflege">Mehr erfahren <ArrowRight size={14} /></Link>
            </div>
            <div className="location-service-card">
              <span className="service-emoji">🪓</span>
              <h3>Baumfällung</h3>
              <p>Sichere Fällung auch in engen Verhältnissen. Wir entfernen problematische Bäume in {locationData.name} fachgerecht.</p>
              <Link to="/leistungen/baumfaellung">Mehr erfahren <ArrowRight size={14} /></Link>
            </div>
            <div className="location-service-card">
              <span className="service-emoji">🚜</span>
              <h3>Erdarbeiten</h3>
              <p>Aushub, Planierung, Vorbereitung für Projekte. Baggerarbeiten in {locationData.name} erledigen wir zuverlässig.</p>
              <Link to="/leistungen/erdarbeiten">Mehr erfahren <ArrowRight size={14} /></Link>
            </div>
            <div className="location-service-card">
              <span className="service-emoji">💦</span>
              <h3>Hochdruckreinigung</h3>
              <p>Terrasse, Einfahrt oder Fassade – wir bringen alles wieder zum Strahlen. Auch in {locationData.name}.</p>
              <Link to="/leistungen/hochdruckreinigung">Mehr erfahren <ArrowRight size={14} /></Link>
            </div>
          </div>
        </div>
      </section>

      {/* Recent projects in this area */}
      <section className="location-projects">
        <div className="container">
          <h2>Aktuelle Projekte in {locationData.name}</h2>
          <div className="location-projects-grid">
            {locationData.recentProjects.map((project, index) => (
              <div key={index} className="location-project-card">
                <div className="project-location-tag">
                  <MapPin size={16} />
                  {locationData.name}
                </div>
                <h3>{project.title}</h3>
                <p>{project.desc}</p>
                <span className="project-trust">Kürzlich in Ihrer Nähe erledigt</span>
              </div>
            ))}
          </div>
          <div className="location-projects-cta">
            <Link to="/projekte" className="btn-outline">
              Alle Projekte ansehen
            </Link>
          </div>
        </div>
      </section>

      {/* Review teaser */}
      <section className="location-reviews">
        <div className="container">
          <div className="location-review-box">
            <div className="review-stars">
              {[1,2,3,4,5].map(i => <Star key={i} size={20} fill="#ffc107" color="#ffc107" />)}
            </div>
            <blockquote>
              "Super Service, schnelle Termine und faire Preise. Kann ich nur empfehlen!"
            </blockquote>
            <cite>— Zufriedener Kunde aus {locationData.name}</cite>
            <Link to="/bewertungen">Alle Bewertungen ansehen →</Link>
          </div>
        </div>
      </section>

      {/* Service Links for this location */}
      <section className="location-service-links">
        <div className="container">
          <h2>Unsere Leistungen in {locationData.name}</h2>
          <div className="location-service-links-grid">
            <Link to={`/gartenpflege/${locationSlug}`} className="loc-service-link-card">
              <h3>Gartenpflege</h3>
              <p>Rasenmähen, Heckenschnitt, Beetpflege</p>
            </Link>
            <Link to={`/erdarbeiten/${locationSlug}`} className="loc-service-link-card">
              <h3>Erdarbeiten</h3>
              <p>Minibagger, Aushub, Drainage</p>
            </Link>
            <Link to={`/baumfaellung/${locationSlug}`} className="loc-service-link-card">
              <h3>Baumfällung</h3>
              <p>Baumpflege, Stubbenfräsen, Kronenschnitt</p>
            </Link>
            <Link to={`/hochdruckreinigung/${locationSlug}`} className="loc-service-link-card">
              <h3>Hochdruckreinigung</h3>
              <p>Terrassen, Einfahrten, Fassaden</p>
            </Link>
            <Link to={`/entsorgung/${locationSlug}`} className="loc-service-link-card">
              <h3>Entsorgung</h3>
              <p>Grünschnitt, Gartenabfälle, Erdaushub</p>
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="location-final-cta">
        <div className="container">
          <h2>Gartenarbeit in {locationData.name}?</h2>
          <p>Wir sind regelmäßig in Ihrer Nähe. Rufen Sie an für ein kostenloses Angebot.</p>
          <a href="tel:+4917655669090" className="phone-big">
            <Phone size={24} />
            0176 - 556 690 90
          </a>
          <div className="final-buttons">
            <Link to="/kontakt" className="btn-cta-large">
              Angebot für {locationData.name} anfragen
            </Link>
          </div>
          <p className="micro-trust">Oft Termine innerhalb weniger Tage möglich</p>
        </div>
      </section>
    </div>
  );
};

export default LocationPage;
